import { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format, parseISO } from "date-fns";
import { it, hr, enGB, de, hu, cs, sl } from "date-fns/locale";
import { TrendingDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getHistory } from "@/lib/api";

const LOCALES = { it, hr, en: enGB, de, hu, cs, sl };
const LABEL = {
  it: "Andamento prezzo · ultime settimane",
  en: "Price trend · last weeks",
  hr: "Kretanje cijene · zadnji tjedni",
  sl: "Gibanje cene · zadnji tedni",
  de: "Preisverlauf · letzte Wochen",
  hu: "Árváltozás · utóbbi hetek",
  cs: "Vývoj ceny · poslední týdny",
};
const LOW = { it: "minimo", en: "low", hr: "najniže", sl: "najnižje", de: "Tief", hu: "minimum", cs: "minimum" };

export const PriceHistory = ({ slug }) => {
  const { lang } = useApp();
  const [data, setData] = useState(null);
  const locale = LOCALES[lang] || enGB;

  useEffect(() => {
    getHistory(slug, 8).then(setData).catch(() => {});
  }, [slug]);

  if (!data) return null;
  const chart = data.points.map((p) => ({ ...p, label: format(parseISO(p.date), "d MMM", { locale }) }));

  return (
    <div className="mt-10" data-testid="price-history">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-petrol">{LABEL[lang]}</h2>
        <span className="flex items-center gap-1.5 rounded-none bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          <TrendingDown size={14} /> {LOW[lang]} €{data.low}
        </span>
      </div>
      <div className="rounded-2xl border border-sand-border bg-sand-card p-4">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chart} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#c85a32" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#c85a32" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#5a7382" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: "#5a7382" }} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => `€${v}`} domain={["dataMin - 20", "dataMax + 20"]} />
            <Tooltip
              contentStyle={{ background: "#0f2d40", border: "none", borderRadius: 10, color: "#f5f2eb" }}
              labelStyle={{ color: "#f5b841" }}
              formatter={(v) => [`€${v}`, ""]}
            />
            <ReferenceLine y={data.low} stroke="#4a7a4a" strokeDasharray="4 4" />
            <Area type="monotone" dataKey="price" stroke="#c85a32" strokeWidth={2.5} fill="url(#pg)" dot={{ r: 3, fill: "#c85a32" }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
